import React, { useEffect, ReactElement } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FocusTrap from "focus-trap-react";

import UTIcon from "./icons";
import Button from "./button";
import { CardProps } from "./card";
import { usePartnerContext } from "../context/partner";

type ModalProps = {
    children?: ReactElement,
    show: boolean,
    header?: string,
    buttons?: ReactElement,
    color?: CardProps["color"],
    icon?: CardProps["icon"],
    padding?: string,
    modalPadding?: string,
    className?: string,
    onClose?: () => void,
    onConfirm?: () => void,
    showXButton?: boolean
    maxWidth?: string
}

const Modal = ({ children, className = "", show, maxWidth, color, header = undefined, buttons = undefined, icon = undefined, padding = "py-5", modalPadding = "px-5", onClose = undefined, onConfirm = () => { }, showXButton }: ModalProps) => {

    const { colors } = usePartnerContext();

    useEffect(() => {
        const value = show ? 'hidden' : 'auto';
        document.querySelector('body')?.setAttribute('style', `overflow: ${value};`);
        return () => {
            // set window overflow auto
            document.querySelector('body')?.setAttribute('style', 'overflow: auto;');
        }
    }, [show])

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && show && onClose) {
                onClose();
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [show, onClose]);

    // Animation variants
    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };

    const modalVariants = {
        hidden: {
            opacity: 0,
            scale: 0.95,
            y: 20,
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                damping: 25,
                stiffness: 300,
            },
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            y: 20,
            transition: {
                duration: 0.2,
            },
        },
    };

    return <AnimatePresence>
        {show && (
            <FocusTrap>
                <motion.div
                    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex justify-center items-center p-4"
                    variants={backdropVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    onClick={onClose}
                >
                    <motion.div
                        className={`relative bg-white ${className} rounded-lg max-h-[85vh]`}
                        style={{
                            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                            maxWidth: maxWidth || "75vw"
                        }}
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={(e) => e.stopPropagation()}
                    >
            {showXButton && (
                <button
                    type="button"
                    className={`
                        absolute -top-2 -right-2 p-2 z-50
                        bg-white rounded-full
                        shadow-lg hover:shadow-xl
                        transition-all duration-150
                        hover:scale-110
                        border border-gray-200
                    `}
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    <UTIcon type={"close"} size={18} color={colors.gray600 || colors.gray9} />
                </button>
            )}
            <div
                className={`
                relative overflow-auto h-full w-full
                space-y-5
                ${padding}
            `}>
                {header && (
                    <div className={"px-5 pb-3 border-b"} style={{ color: colors.textColor, borderColor: colors.border || colors.gray200 }}>
                        <div className={"flex items-center justify-between space-x-3"}>
                            <div className={"flex items-center space-x-3"}>
                                {icon && (
                                    <UTIcon color={colors.textColor} type={icon} size={24} />
                                )}
                                <h2 className={"text-xl font-semibold"}>
                                    {header}
                                </h2>
                            </div>
                            <div>
                                {!showXButton && onClose && (
                                    <Button onClick={onClose} padding={"px-2 py-1"} variant={"ghost"} noShadow>
                                        <UTIcon type={"close"} size={20} />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                <div className={`${modalPadding}`}>
                    {children}
                </div>
                {buttons && (
                    <div className={"px-5 pt-3 border-t"} style={{ borderColor: colors.border || colors.gray200 }}>
                        {buttons}
                    </div>
                )}
            </div>
                </motion.div>
                </motion.div>
            </FocusTrap>
        )}
    </AnimatePresence>
}

export default Modal;